import { max, min, sum } from 'lodash';
import { IAggregateFunction } from 'pages/revisions-module/template-editor/editors/form/types';

/**
 * Checks whether a value should be considered empty.
 */
export function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
}

/**
 * Normalizes values for unique comparison.
 */
export function normalizeValue(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Computes an aggregated value from a list of values using the given aggregation function.
 */
export function computeAggregation(
  values: unknown[],
  aggFunc: IAggregateFunction,
  separator?: string,
  selectedValue?: string
): unknown {
  const numericValues = values.map(Number).filter((raw) => !isNaN(raw));

  switch (aggFunc) {
    case 'count':
      return values.length;

    case 'count-selected':
      if (selectedValue === undefined || selectedValue === null) {
        return 0;
      }

      return values.filter((value) => normalizeValue(value) === normalizeValue(selectedValue)).length;

    case 'sum':
      return sum(numericValues);

    case 'min':
      return min(numericValues);

    case 'max':
      return max(numericValues);

    case 'concat':
      return values.join(separator ?? '');

    case 'concat-unique':
      return [...new Set(values.map(normalizeValue))].join(separator ?? '');

    case 'frequency-expression': {
      const frequencies = values.map(normalizeValue).reduce<Map<string, number>>((counts, value) => {
        counts.set(value, (counts.get(value) ?? 0) + 1);
        return counts;
      }, new Map());

      return [...frequencies.entries()]
        .sort()
        .map(([value, count]) => (count > 1 ? `${count}x${value}` : value))
        .join(' + ');
    }

    case 'numberOfUniqueValues':
      return new Set(values.map(normalizeValue)).size;

    case 'numberOfNonEmptyValues':
      return values.filter((v) => !isEmptyValue(v)).length;

    case 'numberOfEmptyValues':
      return values.filter((v) => isEmptyValue(v)).length;

    default:
      return 0;
  }
}
